import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, PhoneCall, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CITY_DATA,
  SERVICES,
  SITE,
  getAlternateLanguagePath,
  getCityServicePath,
  getNavItems,
  getServicePath,
  getSpecialServicePath,
} from "@/data/siteContent";

const SiteLayout = ({ language, children }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navItems = getNavItems(language);
  const isFr = language === "fr";
  const alternatePath = getAlternateLanguagePath(location.pathname, language);
  const cityLinks = CITY_DATA.map((city) => ({
    slug: city.slug,
    label: isFr ? city.nameFr : city.nameEn,
    repairPath: getCityServicePath(language, city.slug, "repair"),
    installationPath: getCityServicePath(language, city.slug, "installation"),
  }));
  const languageLinks = [
    {
      code: "FR",
      path: language === "fr" ? location.pathname : alternatePath,
      active: language === "fr",
    },
    {
      code: "EN",
      path: language === "en" ? location.pathname : alternatePath,
      active: language === "en",
    },
  ];

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center gap-6">
          <div className="flex items-center justify-between w-full lg:w-auto lg:flex-1">
            <Link
              to={isFr ? "/fr" : "/en"}
              className="flex flex-col gap-1 min-w-[180px] lg:min-w-[230px]"
              data-testid="site-logo-link"
            >
              <span className="text-base md:text-lg font-bold text-primary tracking-[0.06em] uppercase leading-tight">
                <span className="hidden lg:inline whitespace-nowrap">
                  Réparation de pavé uni Rive-Sud
                </span>
                <span className="block lg:hidden">Réparation de pavé uni</span>
                <span className="block lg:hidden">Rive-Sud</span>
              </span>
              <span
                className="text-[0.7rem] md:text-xs text-muted-foreground"
                data-testid="site-logo-tagline"
              >
                {isFr
                  ? "Spécialistes du pavé uni résidentiel"
                  : "Residential paver specialists"}
              </span>
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="p-2 border border-border text-primary lg:hidden"
              data-testid="mobile-menu-button"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          <nav className="hidden lg:flex flex-1 items-center justify-center gap-3" data-testid="desktop-nav">
            {navItems.map((item) => {
              if (item.id === "repair" || item.id === "installation") {
                const isRepair = item.id === "repair";
                const dropdownLinks = cityLinks.map((city) => ({
                  label: city.label,
                  path: isRepair ? city.repairPath : city.installationPath,
                }));
                return (
                  <div
                    key={item.id}
                    className="relative group"
                    data-testid={`nav-${language}-${item.id}-dropdown`}
                  >
                    <Link
                      to={item.path}
                      className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-foreground hover:text-secondary transition whitespace-nowrap"
                      data-testid={`nav-${language}-${item.id}-link`}
                    >
                      {item.label}
                    </Link>
                    <div className="absolute left-1/2 top-full z-20 w-[320px] -translate-x-1/2 pt-3 opacity-0 pointer-events-none transition group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto">
                      <div className="rounded-sm border border-border bg-white shadow-xl p-4 space-y-3">
                        <Link
                          to={item.path}
                          className="text-xs font-semibold uppercase tracking-[0.2em] text-primary"
                          data-testid={`nav-${language}-${item.id}-overview-link`}
                        >
                          {isFr
                            ? isRepair
                              ? "Réparation pavé uni"
                              : "Installation pavé uni"
                            : isRepair
                              ? "Paver repair overview"
                              : "Paver installation overview"}
                        </Link>
                        <div className="grid grid-cols-2 gap-2">
                          {dropdownLinks.map((link) => (
                            <Link
                              key={`${item.id}-${link.label}`}
                              to={link.path}
                              className="text-xs text-muted-foreground hover:text-secondary hover:underline hover:underline-offset-4 hover:decoration-secondary/40 transition"
                              data-testid={`nav-${language}-${item.id}-${link.label
                                .toLowerCase()
                                .replace(/\s+/g, "-")}-link`}
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-foreground hover:text-secondary transition whitespace-nowrap"
                  data-testid={`nav-${language}-${item.id}-link`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex flex-1 items-center justify-end gap-3">
            <a
              href={`tel:${SITE.phone}`}
              className="flex items-center gap-2 text-xs font-semibold text-primary whitespace-nowrap"
              data-testid="header-phone-link"
            >
              <PhoneCall className="h-3 w-3" />
              {SITE.phoneDisplay}
            </a>
            <Button
              asChild
              className="rounded-none uppercase tracking-wider font-bold px-3 text-[0.65rem]"
              data-testid="header-cta-button"
            >
              <Link to={isFr ? "/fr/contact" : "/en/contact"}>
                {isFr ? "Soumission gratuite" : "Free estimate"}
              </Link>
            </Button>
            <div className="flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-secondary" data-testid="language-toggle">
              {languageLinks.map((item, index) => (
                <span key={item.code} className="flex items-center gap-2">
                  <Link
                    to={item.path}
                    className={item.active ? "text-primary" : "text-secondary"}
                    data-testid={`language-toggle-${item.code.toLowerCase()}`}
                  >
                    {item.code}
                  </Link>
                  {index === 0 && <span className="text-muted-foreground">|</span>}
                </span>
              ))}
            </div>
          </div>
        </div>

        {menuOpen && (
          <div
            className="lg:hidden border-t border-border bg-white"
            data-testid="mobile-menu-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <div className="flex items-center justify-between">
                <a
                  href={`tel:${SITE.phone}`}
                  className="text-sm font-semibold text-primary"
                  data-testid="mobile-menu-phone-link"
                >
                  {SITE.phoneDisplay}
                </a>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-secondary" data-testid="mobile-language-toggle">
                  {languageLinks.map((item, index) => (
                    <span key={item.code} className="flex items-center gap-2">
                      <Link
                        to={item.path}
                        className={item.active ? "text-primary" : "text-secondary"}
                        data-testid={`mobile-language-toggle-${item.code.toLowerCase()}`}
                      >
                        {item.code}
                      </Link>
                      {index === 0 && <span className="text-muted-foreground">|</span>}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-4">
                {navItems.map((item) => {
                  if (item.id === "repair" || item.id === "installation") {
                    const isRepair = item.id === "repair";
                    const dropdownLinks = cityLinks.map((city) => ({
                      label: city.label,
                      path: isRepair ? city.repairPath : city.installationPath,
                    }));
                    return (
                      <div key={item.id} className="space-y-2">
                        <Link
                          to={item.path}
                          className="text-sm font-semibold uppercase tracking-widest text-foreground whitespace-nowrap"
                          data-testid={`mobile-nav-${language}-${item.id}-link`}
                        >
                          {item.label}
                        </Link>
                        <div className="ml-4 flex flex-col gap-2">
                          {dropdownLinks.map((link) => (
                            <Link
                              key={`${item.id}-${link.label}`}
                              to={link.path}
                              className="text-xs text-muted-foreground whitespace-nowrap hover:underline hover:underline-offset-4 hover:decoration-secondary/40"
                              data-testid={`mobile-nav-${language}-${item.id}-${link.label
                                .toLowerCase()
                                .replace(/\s+/g, "-")}-link`}
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className="text-sm font-semibold uppercase tracking-widest text-foreground whitespace-nowrap"
                      data-testid={`mobile-nav-${language}-${item.id}-link`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
              <Button
                asChild
                className="rounded-none uppercase tracking-wider font-bold"
                data-testid="mobile-nav-cta-button"
              >
                <Link to={isFr ? "/fr/contact" : "/en/contact"}>
                  {isFr ? "Soumission gratuite" : "Free estimate"}
                </Link>
              </Button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pb-24 md:pb-0">{children}</main>

      <footer className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 grid gap-12 lg:grid-cols-3">
          <div className="space-y-4">
            <p
              className="text-sm font-semibold uppercase tracking-widest"
              data-testid="footer-contact-title"
            >
              {isFr ? "Contact" : "Contact"}
            </p>
            <p className="text-sm text-slate-200" data-testid="footer-tagline">
              {isFr
                ? "Entreprise locale spécialisée en réparation et installation de pavé uni."
                : "Local experts in paver repair and installation."}
            </p>
            <div className="space-y-2 text-sm text-slate-200">
              <span data-testid="footer-address">{SITE.address}</span>
              <a
                href={`tel:${SITE.phone}`}
                className="block"
                data-testid="footer-phone-link"
              >
                {SITE.phoneDisplay}
              </a>
              <a
                href={`mailto:${SITE.email}`}
                className="block hover:underline hover:underline-offset-4 hover:decoration-secondary/40"
                data-testid="footer-email-link"
              >
                {SITE.email}
              </a>
            </div>
          </div>

          <div className="space-y-4">
            <p
              className="text-sm font-semibold uppercase tracking-widest"
              data-testid="footer-services-title"
            >
              {isFr ? "Services" : "Services"}
            </p>
            <div className="flex flex-col gap-2 text-sm text-slate-200">
              <Link
                to={getServicePath(language, "repair")}
                className="hover:underline hover:underline-offset-4 hover:decoration-secondary/40"
                data-testid="footer-service-repair-link"
              >
                {isFr ? "Réparation de pavé uni" : "Paver repair"}
              </Link>
              <Link
                to={getServicePath(language, "installation")}
                className="hover:underline hover:underline-offset-4 hover:decoration-secondary/40"
                data-testid="footer-service-installation-link"
              >
                {isFr ? "Installation de pavé uni" : "Paver installation"}
              </Link>
              <Link
                to={getSpecialServicePath(language, "cleaning")}
                className="hover:underline hover:underline-offset-4 hover:decoration-secondary/40"
                data-testid="footer-service-leveling-link"
              >
                {isFr ? "Nivelage et remise à neuf" : "Leveling and restoration"}
              </Link>
              <Link
                to={getSpecialServicePath(language, "sealing")}
                className="hover:underline hover:underline-offset-4 hover:decoration-secondary/40"
                data-testid="footer-service-maintenance-link"
              >
                {isFr ? "Entretien et scellant" : "Maintenance and sealing"}
              </Link>
            </div>
          </div>

          <div className="space-y-4">
            <p
              className="text-sm font-semibold uppercase tracking-widest"
              data-testid="footer-location-title"
            >
              {isFr ? "Localisation – Rive-Sud" : "Location – South Shore"}
            </p>
            <div className="space-y-2 text-sm text-slate-200" data-testid="footer-service-area-section">
              <h3 className="text-sm font-semibold text-white" data-testid="footer-service-area-title">
                {isFr ? "Zone desservie – Rive-Sud" : "Service area – South Shore"}
              </h3>
              <p data-testid="footer-service-area-description">
                {isFr
                  ? "Nous offrons la réparation, l'installation et le nettoyage de pavé uni sur la Rive-Sud :"
                  : "We provide paver repair, installation, and cleaning across the South Shore:"}
              </p>
             <p className="flex flex-wrap gap-x-2 gap-y-1" data-testid="footer-service-area-links">
  {CITY_DATA.map((city, index, arr) => {
    const path = isFr
      ? `/fr/villes/${city.slug}/reparation-pave-uni`
      : `/en/cities/${city.slug}/paver-repair`;
    const label = isFr ? city.nameFr : city.nameEn;
    return (
      <span key={city.slug} className="inline-flex items-center gap-x-2">
        <Link
          to={path}
          className="hover:underline hover:underline-offset-4 hover:decoration-secondary/40"
          data-testid={`footer-service-area-link-${city.slug}${isFr ? "-fr" : "-en"}`}
        >
          {label}
        </Link>
        {index < arr.length - 1 && <span aria-hidden="true">·</span>}
      </span>
    );
  })}
</p>
            </div>
            <div
              className="overflow-hidden rounded-2xl border border-white/10"
              data-testid="footer-location-map"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2797.5!2d-73.2989403!3d45.3537693!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc9a11c638428ed%3A0xd68559a76273e429!2sR%C3%A9paration+pav%C3%A9+uni+Rive-Sud!5e0!3m2!1sfr!2sca!4v1"
                width="100%"
                height="220"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Carte Google Maps – Rive-Sud de Montréal"
                data-testid="footer-location-map-iframe"
              />
            </div>
            <a
              href="https://maps.app.goo.gl/qy3ZcTazaXXDEEsX7"
              target="_blank"
              rel="noreferrer"
              className="text-sm font-semibold text-secondary hover:underline hover:underline-offset-4 hover:decoration-secondary/40"
              data-testid="footer-location-map-link"
            >
              {isFr ? "Ouvrir dans Google Maps" : "Open in Google Maps"}
            </a>
          </div>
        </div>
        <div className="border-t border-slate-700 py-6 text-center text-xs text-slate-400" data-testid="footer-copyright">
          © Groupe Stuctera – Tous droits réservés
        </div>
      </footer>

      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-primary text-white shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 text-sm font-semibold">
          <a
            href={`tel:${SITE.phone}`}
            className="flex items-center gap-2"
            data-testid="mobile-cta-call-link"
          >
            <PhoneCall className="h-4 w-4" />
            {isFr ? "Appeler" : "Call now"}
          </a>
          <Link
            to={isFr ? "/fr/contact" : "/en/contact"}
            className="uppercase tracking-wider"
            data-testid="mobile-cta-quote-link"
          >
            {isFr ? "Soumission" : "Get quote"}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SiteLayout;